import React, { useEffect, useState, useContext } from "react";
import AdminLayout from "../../Components/admin/adminLayout";
import { AuthContext } from "../../contextStore/AuthContext";
import client from "../../api/client";

/**
 * Seller Plans management (MRP + Discounted only)
 */

export default function SellerPlansPage() {
  const { token } = useContext(AuthContext);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  function getAuthHeaders() {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line
  }, []);

  function centsToDecimal(cents) {
    if (cents === null || typeof cents === "undefined" || cents === "")
      return "";
    const n = Number(cents);
    if (!Number.isFinite(n)) return "";
    return (n / 100).toFixed(2);
  }

  function decimalToCents(val) {
    if (val === null || typeof val === "undefined" || val === "") return null;
    const n = Number(val);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100);
  }

  async function loadPlans() {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await client.get("/api/admin/seller-plans", {
        headers: getAuthHeaders(),
      });
      if (res.data && res.data.ok) {
        const normalized = (res.data.plans || []).map((p) => ({
          ...p,
          mrp_cents:
            typeof p.mrp_cents !== "undefined" && p.mrp_cents !== null
              ? Number(p.mrp_cents)
              : null,
          mrp_display: p.mrp_cents == null ? "" : centsToDecimal(p.mrp_cents),
          discounted_price_cents:
            typeof p.discounted_price_cents !== "undefined" &&
            p.discounted_price_cents !== null
              ? Number(p.discounted_price_cents)
              : null,
          discounted_display:
            p.discounted_price_cents == null
              ? ""
              : centsToDecimal(p.discounted_price_cents),
          plan_mode: p.plan_mode || "per_post",
          duration_days: p.duration_days ? Number(p.duration_days) : null,
          max_posts_per_purchase: p.max_posts_per_purchase
            ? Number(p.max_posts_per_purchase)
            : null,
          is_active: p.is_active === 1 || p.is_active === true ? 1 : 0,
          description: p.description || "",
        }));
        setPlans(normalized);
      } else {
        setPlans([]);
        setFetchError("No plans returned from server.");
      }
    } catch (err) {
      console.error("Failed to fetch seller plans:", err);
      const msg =
        (err.response &&
          (err.response.data?.message || err.response.data?.error)) ||
        err.message ||
        "Error loading plans";
      setPlans([]);
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  }

  function openAdd() {
    setEditing({
      name: "",
      plan_mode: "per_post",
      duration_days: null,
      mrp_cents: null,
      discounted_price_cents: null,
      max_posts_per_purchase: 1,
      description: "",
      is_active: 1,
    });
    setShowForm(true);
    setError(null);
  }

  function openEdit(p) {
    setEditing({
      ...p,
      mrp_cents:
        typeof p.mrp_cents !== "undefined" && p.mrp_cents !== null
          ? Number(p.mrp_cents)
          : null,
      discounted_price_cents:
        typeof p.discounted_price_cents !== "undefined" &&
        p.discounted_price_cents !== null
          ? Number(p.discounted_price_cents)
          : null,
      plan_mode: p.plan_mode || "per_post",
      duration_days: p.duration_days ? Number(p.duration_days) : null,
      max_posts_per_purchase: p.max_posts_per_purchase
        ? Number(p.max_posts_per_purchase)
        : null,
      is_active: p.is_active === 1 || p.is_active === true ? 1 : 0,
      description: p.description || "",
    });
    setShowForm(true);
    setError(null);
  }

  async function savePlan(payload) {
    setError(null);
    try {
      const body = {
        name: payload.name,
        description: payload.description || "",
        is_active: payload.is_active ? 1 : 0,
        mrp_cents:
          payload.mrp_cents === null || typeof payload.mrp_cents === "undefined"
            ? null
            : Number(payload.mrp_cents),
        discounted_price_cents:
          payload.discounted_price_cents === null ||
          typeof payload.discounted_price_cents === "undefined"
            ? null
            : Number(payload.discounted_price_cents),
        plan_mode: payload.plan_mode || "per_post",
        duration_days:
          typeof payload.duration_days !== "undefined"
            ? payload.duration_days === ""
              ? null
              : Number(payload.duration_days)
            : null,
        max_posts_per_purchase:
          typeof payload.max_posts_per_purchase !== "undefined"
            ? payload.max_posts_per_purchase === ""
              ? null
              : Number(payload.max_posts_per_purchase)
            : null,
      };

      let res;
      if (payload.id) {
        res = await client.put(`/api/admin/seller-plans/${payload.id}`, body, {
          headers: getAuthHeaders(),
        });
      } else {
        res = await client.post(`/api/admin/seller-plans`, body, {
          headers: getAuthHeaders(),
        });
      }

      if (res.data && res.data.ok) {
        setShowForm(false);
        setEditing(null);
        loadPlans();
      } else {
        setError(
          (res.data && (res.data.error || res.data.message)) ||
            "Failed to save plan"
        );
      }
    } catch (err) {
      console.error("savePlan err", err);
      const msg =
        (err.response &&
          (err.response.data?.message || err.response.data?.error)) ||
        err.message ||
        "server_error";
      setError(msg);
    }
  }

  async function toggleActive(plan) {
    try {
      const newActive = plan.is_active ? 0 : 1;
      await client.put(
        `/api/admin/seller-plans/${plan.id}`,
        { is_active: newActive },
        { headers: getAuthHeaders() }
      );
      loadPlans();
    } catch (err) {
      console.error("toggleActive", err);
      alert(
        (err.response &&
          (err.response.data?.error || err.response.data?.message)) ||
          "Failed to toggle active"
      );
    }
  }

  async function deactivate(id) {
    if (!window.confirm("Delete / Deactivate this plan?")) return;
    try {
      await client.delete(`/api/admin/seller-plans/${id}`, {
        headers: getAuthHeaders(),
      });
      loadPlans();
    } catch (err) {
      console.error("deactivate", err);
      alert(
        (err.response &&
          (err.response.data?.error || err.response.data?.message)) ||
          "Failed to deactivate/delete"
      );
    }
  }

  const activeCount = plans.filter(
    (p) => p.is_active == 1 || p.is_active === true
  ).length;

  return (
    <AdminLayout>
      <h2>Seller Plans</h2>

      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 12,
          alignItems: "center",
        }}
      >
        <div style={{ marginLeft: "auto" }}>
          <button onClick={openAdd} disabled={activeCount >= 3}>
            Add Plan
          </button>
          {activeCount >= 3 && (
            <small style={{ marginLeft: 8, color: "#666" }}>
              Max 3 active plans
            </small>
          )}
        </div>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : fetchError ? (
        <div style={{ color: "red", marginBottom: 12 }}>{fetchError}</div>
      ) : (
        <table
          border="1"
          cellPadding="8"
          style={{ background: "#fff", width: "100%" }}
        >
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Plan Mode / Duration</th>
              <th>MRP / Discounted</th>
              <th>Max posts</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td>{p.name}</td>
                <td>
                  {p.plan_mode === "per_post"
                    ? `Per-post (${p.max_posts_per_purchase || "-"} posts)`
                    : `${p.duration_days || "-"} days`}
                </td>
                <td>
                  <div style={{ fontSize: 14 }}>
                    MRP: {p.mrp_display || "-"}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: "bold" }}>
                    Discounted: {p.discounted_display || "-"}
                  </div>
                </td>
                <td>
                  {p.max_posts_per_purchase
                    ? `${p.max_posts_per_purchase} posts`
                    : "Per-post"}
                </td>
                <td>{p.is_active ? "Yes" : "No"}</td>
                <td style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => openEdit(p)}>Edit</button>
                  <button onClick={() => toggleActive(p)}>
                    {p.is_active ? "Deactivate" : "Activate"}
                  </button>
                  <button onClick={() => deactivate(p.id)}>Delete</button>
                </td>
              </tr>
            ))}

            {plans.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center" }}>
                  No seller plans found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {showForm && editing && (
        <div
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: 20,
              width: 720,
              borderRadius: 6,
            }}
          >
            <h3>{editing.id ? "Edit Plan" : "Add Plan"}</h3>
            {error && <div style={{ color: "red" }}>{error}</div>}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <label>
                Name
                <input
                  value={editing.name || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, name: e.target.value })
                  }
                />
              </label>

              <div />

              <label>
                Plan Mode
                <select
                  value={editing.plan_mode || "per_post"}
                  onChange={(e) =>
                    setEditing({ ...editing, plan_mode: e.target.value })
                  }
                >
                  <option value="per_post">Per-post (package)</option>
                  <option value="duration">Duration-based</option>
                </select>
              </label>

              <label>
                Duration days (optional)
                <input
                  type="number"
                  value={editing.duration_days || ""}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      duration_days: e.target.value
                        ? parseInt(e.target.value, 10)
                        : null,
                    })
                  }
                  placeholder="e.g. 30"
                />
              </label>

              <label>
                Max posts per purchase
                <input
                  type="number"
                  value={editing.max_posts_per_purchase || ""}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      max_posts_per_purchase: e.target.value
                        ? parseInt(e.target.value, 10)
                        : null,
                    })
                  }
                  placeholder="e.g. 1"
                />
              </label>

              <label>
                MRP (decimal)
                <input
                  type="number"
                  step="0.01"
                  value={
                    editing.mrp_cents !== null &&
                    typeof editing.mrp_cents !== "undefined"
                      ? (Number(editing.mrp_cents) / 100).toFixed(2)
                      : ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      mrp_cents:
                        e.target.value !== ""
                          ? decimalToCents(e.target.value)
                          : null,
                    })
                  }
                />
              </label>

              <label>
                Discounted price (decimal)
                <input
                  type="number"
                  step="0.01"
                  value={
                    editing.discounted_price_cents !== null &&
                    typeof editing.discounted_price_cents !== "undefined"
                      ? (Number(editing.discounted_price_cents) / 100).toFixed(
                          2
                        )
                      : ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      discounted_price_cents:
                        e.target.value !== ""
                          ? decimalToCents(e.target.value)
                          : null,
                    })
                  }
                />
              </label>

              <label style={{ alignSelf: "center" }}>
                Active
                <input
                  type="checkbox"
                  checked={!!editing.is_active}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      is_active: e.target.checked ? 1 : 0,
                    })
                  }
                />
              </label>
            </div>

            <div style={{ marginTop: 8 }}>
              <label>
                Description
                <textarea
                  value={editing.description || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, description: e.target.value })
                  }
                  style={{ width: "100%", minHeight: 80 }}
                />
              </label>

              <div style={{ marginTop: 8, color: "#666", fontSize: 12 }}>
                Note: For seller plans we collect MRP and Discounted price (both
                stored as cents).
              </div>
            </div>

            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button onClick={() => savePlan({ ...editing })}>Save</button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditing(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}